// src/components/dashboard/VMDashboard.jsx
import { useEffect, useMemo } from 'react';
import { Box, Container } from '@mui/material';
import CustomerSummary from './CustomerSummary';
import RevenueChecker from './RevenueChecker';
import ReasonStatistics from './ReasonStatistics';
import VisitorsStatistics from './VisitorsStatistics';
import TaskChecklist from './TaskChecklist';
import EnquiriesStatistics from './EnquiriesStatistics';
import { getEndOfCurrentMonth, getStartOfCurrentMonth } from '../../Util/UtilHelper';
import StorageFactory from '../../Util/StorageFactory';
import { SESSION_USER_ID_KEY } from '../../Util/VMConstants';
import MUITypography from '../../Components/MUITypography';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSubscriptionStatus } from '../../Redux/slices/subscriptionSlice';
import { canAccessDashboardPremiumFeatures } from '../../Util/subscriptionTierHelper';

const VMDashboard = () => {
  const dispatch = useDispatch();
  const subscriptionStatus = useSelector((state) => state.subscription.status);

  const canAccessPremiumFeatures = useMemo(
    () => canAccessDashboardPremiumFeatures(subscriptionStatus),
    [subscriptionStatus]
  );

  const defaultFrom = useMemo(() => getStartOfCurrentMonth(), []);
  const defaultTo = useMemo(() => getEndOfCurrentMonth(), []);

  const sessionFactory = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return new StorageFactory('session');
  }, []);

  const userID = sessionFactory?.get(SESSION_USER_ID_KEY) ?? null;

  useEffect(() => {
    if (!userID) return;
    dispatch(fetchSubscriptionStatus(userID));
  }, [dispatch, userID]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        m: { xs: 0, sm: 0, md: 2, lg: 2 },
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
        <MUITypography
          sx={{
            mb: 4,
            fontSize: { xs: '18px', sm: '20px' },
            fontWeight: 700,
          }}
        >
          Dashboard
        </MUITypography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            gap: 2,
            width: '100%',
            mb: 3,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
            <CustomerSummary
              defaultFrom={defaultFrom}
              defaultTo={defaultTo}
              userID={userID}
              canAccessPremiumFeatures={canAccessPremiumFeatures}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
            <RevenueChecker
              defaultFrom={defaultFrom}
              defaultTo={defaultTo}
              userID={userID}
            />
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 2,
          }}
        >
          <ReasonStatistics
            defaultFrom={defaultFrom}
            defaultTo={defaultTo}
            userID={userID}
          />
          <VisitorsStatistics
            defaultFrom={defaultFrom}
            defaultTo={defaultTo}
            userID={userID}
            canAccessPremiumFeatures={canAccessPremiumFeatures}
          />
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 2,
          }}
        >
          <TaskChecklist
            defaultFrom={defaultFrom}
            defaultTo={defaultTo}
            userID={userID}
          />
          <EnquiriesStatistics
            defaultFrom={defaultFrom}
            defaultTo={defaultTo}
            userID={userID}
            canAccessPremiumFeatures={canAccessPremiumFeatures}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default VMDashboard;
